import type { ConfigProps, MenuItem } from '../../configTypes';
import { NavigationMenuItemFragment } from '../fragments/NavigationMenuItem';
import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { SeoPostFragment } from '../fragments/SEO';
import { TaxonomyFragment } from '../fragments/Taxonomy';
import { ContributorsFragment } from '../fragments/Contributors';
import { MoneyMetaFragment } from '../fragments/MoneyMeta';
import enrichContent from '../enrich/enrichContent';
import transformSeo, { type TransformedSeo } from '../transform/transformSEO';
import { executeGraphQlRequestWithErrors } from './executeGraphQlRequest';
import { describeGraphQlErrors, hasErrorForField } from '../utils';
import imageTransformationOnComponents from '../transform/imageTransformationOnComponents';
import transformTableMarkup from '../transform/transformTableMarkup';
import type { EnrichedContent } from '../types';
export interface PostNode {
  nodeByUri: Record<string, unknown>;
  generalSettings?: Record<string, string>;
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  siteSettings?: ConfigProps;
  secondaryMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
  enrichedContent?: EnrichedContent | null;
  transformedSEO?: TransformedSeo;
}

function pageQuery(config: ConfigProps): string {
  const { postTypeGql } = config;
  return `
    ${NavigationMenuItemFragment()}
    ${FeaturedImageFragment(postTypeGql)}
    ${SeoPostFragment(postTypeGql)}
    ${TaxonomyFragment(postTypeGql)}
    ${ContributorsFragment(postTypeGql)}
    ${MoneyMetaFragment(postTypeGql)}
    query Node(
      $uri: String!
      $headerMenuLocation: MenuLocationEnum
      $footerMenuLocation: MenuLocationEnum
      $secondaryMenuLocation: MenuLocationEnum
      $auxiliaryMenuLocation: MenuLocationEnum
    ) {
      __typename
      nodeByUri(uri: $uri) {
        ... on ${postTypeGql} {
          id
          databaseId
          title
          content
          date: customDate
          publishedDate: date
          modified
          pageTemplate
          customDateType
          uri
          additionalBylineDescription
          sidebarContent
          displayFeaturedImage
          topAdURL
          topMobileAdURL
          topDestinationURL
          sideAdURL
          sideDestinationURL
          topAdImageAlt
          sideAdImageAlt
          hero {
            subtitle
            heroBackgroundColor
            showActionButton
          }
          ...FeaturedImageFragment
          ...SeoPostFragment
          ...TaxonomyFragment
          ...MoneyMetaFragment
          ...ContributorsFragment
        }
      }
      generalSettings { title description }
      headerMenuItems: menuItems(where: { location: $headerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      footerMenuItems: menuItems(where: { location: $footerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      secondaryMenuItems: menuItems(where: { location: $secondaryMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      auxiliaryMenuItems: menuItems(where: { location: $auxiliaryMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
    }
  `;
}

/**
 * Normalize URI for WPGraphQL nodeByUri (expects leading slash for paths).
 * Empty/whitespace becomes "/". Other values are passed through so tenant
 * homepageGql (e.g. "home" or "/") matches what the CMS expects.
 */
function normalizeUri(uri: string): string {
  const trimmed = uri.trim();
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

type FetchPostGraphQLData = {
  nodeByUri: unknown;
  generalSettings?: Record<string, string>;
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  secondaryMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
};

export async function fetchPost(
  config: ConfigProps,
  uri: string,
): Promise<PostNode | null> {
  const {
    cmsUrl,
    headerMenuLocation,
    footerMenuLocation,
    secondaryMenuLocation,
    auxiliaryMenuLocation,
  } = config;
  const query = pageQuery(config);
  const normalizedUri = normalizeUri(uri);
  const variables = {
    uri: normalizedUri,
    headerMenuLocation,
    footerMenuLocation,
    secondaryMenuLocation,
    auxiliaryMenuLocation,
  };

  const { data, errors } =
    await executeGraphQlRequestWithErrors<FetchPostGraphQLData>({
      cmsUrl,
      query,
      variables,
    });

  if (!data) {
    throw new Error(
      `[fetchPost] WordPress returned no data for ${normalizedUri}`,
    );
  }

  const post = data.nodeByUri ?? null;
  if (!post || typeof post !== 'object') {
    if (hasErrorForField(errors, 'nodeByUri')) {
      throw new Error(
        `[fetchPost] WordPress failed to resolve ${normalizedUri}: ${describeGraphQlErrors(errors)}`,
      );
    }
    if (process.env['NODE_ENV'] === 'development') {
      console.warn(
        '[fetchPost] No post found. URI:',
        normalizedUri,
        'cmsUrl:',
        cmsUrl,
      );
    }
    return null;
  }

  const postObj = post as Record<string, unknown>;
  const generalSettings = data.generalSettings;

  try {
    const enrichedContent = await enrichContent(
      postObj as {
        content?: string;
        contributors?: unknown;
        customDateType?: string;
        date?: string;
        additionalBylineDescription?: string;
        featuredImage?: unknown;
        seo?: { canonicalUrl?: string; openGraph?: { description?: string } };
        title?: string;
      },
      config,
    );

    const transformedContent = imageTransformationOnComponents(
      (postObj['content'] as string) || '',
    );
    (postObj as Record<string, unknown>)['content'] =
      transformTableMarkup(transformedContent);

    const rawSidebar = (postObj['sidebarContent'] as string) || '';
    if (rawSidebar) {
      (postObj as Record<string, unknown>)['sidebarContent'] =
        transformTableMarkup(imageTransformationOnComponents(rawSidebar));
    }

    const transformedSEO = transformSeo({
      generalSettings,
      pageSeo: postObj['seo'] as Parameters<typeof transformSeo>[0]['pageSeo'],
      config,
      uri: (postObj['uri'] as string) || '',
    });

    return {
      nodeByUri: postObj,
      generalSettings,
      headerMenuItems: data.headerMenuItems,
      footerMenuItems: data.footerMenuItems,
      secondaryMenuItems: data.secondaryMenuItems,
      auxiliaryMenuItems: data.auxiliaryMenuItems,
      siteSettings: config,
      enrichedContent,
      transformedSEO,
    };
  } catch (error) {
    console.error('[fetchPost]', normalizedUri, error);
    throw error;
  }
}

export default fetchPost;
